import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import Qrisk3Disclaimer from './Qrisk3Disclaimer';
import {
  CONDITION_FIELDS,
  INPUT_LABELS,
  MODEL_NAME,
  entryReasonText,
  inputValueText,
  noteText,
  sourceText
} from './qrisk3Text';
import { personLabel } from '../triage/triagePeople';

// The order a result's inputs are listed in.
const FIELDS = [
  'sex',
  'age',
  'ethnicity',
  'bmi',
  'heightCm',
  'weightKg',
  'smoking',
  'systolicBp',
  'systolicBpSd',
  'cholesterolHdlRatio',
  'townsend',
  'familyHistoryChd',
  'diabetes',
  ...CONDITION_FIELDS.map((c) => c.key)
];

/** The score, as large as the screen allows, with what it is. */
export function Qrisk3Score({ score, calculatedAt }) {
  const t = useAutoTranslation();
  const fmtDate = useDateFormatter();
  const when = toLocalDateFromInstant(calculatedAt);
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('10-year risk of a heart attack or stroke')}
      </Typography>
      <Typography
        sx={{ fontSize: 36, fontWeight: 700, lineHeight: 1.2 }}
        data-testid="qrisk3-score"
      >
        {t('{{score}}%', { score: Number(score).toFixed(1) })}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {when
          ? t('{{model}}, calculated {{date}}', {
              model: MODEL_NAME,
              date: fmtDate(when)
            })
          : MODEL_NAME}
      </Typography>
    </Box>
  );
}

/** Who decided the family history, and with what. */
export function FamilyHistoryEvidence({
  relatives,
  memberRecords,
  anonymous = true
}) {
  const t = useAutoTranslation();
  if (!relatives?.length) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {relatives.map((r) => {
        const who = personLabel(
          {
            memberId: r.memberId,
            familyMemberId: r.familyMemberId,
            pid: r.pid,
            sex: r.sex,
            relation: { kind: r.kind, degree: 1, side: [] }
          },
          { memberRecords, anonymous, t }
        );
        return (
          <Box component="li" key={`${r.memberId || r.pid}`} sx={{ mb: 0.5 }}>
            <Typography variant="body2">
              {r.assumedFull
                ? t(
                    '{{person}} (a full sibling only if the other parent is shared)',
                    {
                      person: who
                    }
                  )
                : who}
            </Typography>
            {(r.entries || []).map((e, i) => (
              <Typography
                key={`${e.codeId}-${i}`}
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary' }}
              >
                {e.age !== null && e.age !== undefined
                  ? t('{{condition}} at {{age}}', {
                      condition: e.name || e.codeId,
                      age: e.age
                    })
                  : e.ageText
                  ? t('{{condition}}, age recorded as "{{age}}"', {
                      condition: e.name || e.codeId,
                      age: e.ageText
                    })
                  : t('{{condition}}, no age recorded', {
                      condition: e.name || e.codeId
                    })}
                {!e.counted && e.reason && (
                  <>
                    {' '}
                    {t('(not counted: {{reason}})', {
                      reason: entryReasonText(e.reason, t)
                    })}
                  </>
                )}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * A stored QRISK3 calculation (riskfactors.qrisk3Result), read only: the score,
 * every input with where it came from, the relatives behind the family history,
 * the notes, and ClinRisk's disclaimer beneath.
 */
export default function Qrisk3StoredResult({
  result,
  memberRecords,
  anonymous = true
}) {
  const t = useAutoTranslation();
  const fmtDate = useDateFormatter();
  if (!result) return null;
  const inputs = result.inputs || {};
  const sources = result.sources || {};
  const notes = (result.warnings || [])
    .map((w) => noteText(w, t))
    .filter(Boolean);
  const rows = FIELDS.filter(
    (f) =>
      inputs[f] !== undefined &&
      !(f === 'erectileDysfunction' && inputs.sex !== 'male')
  );
  return (
    <Box>
      <Qrisk3Score score={result.score} calculatedAt={result.calculatedAt} />
      {notes.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {notes.map((n) => (
            <Typography key={n} variant="body2">
              {n}
            </Typography>
          ))}
        </Box>
      )}
      <Divider sx={{ my: 1 }} />
      <Typography variant="subtitle2" gutterBottom>
        {t('Inputs used')}
      </Typography>
      {rows.map((field) => (
        <Box
          key={field}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            py: 0.25
          }}
        >
          <Typography variant="body2">{t(INPUT_LABELS[field])}</Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2">
              {inputValueText(field, inputs[field], t)}
            </Typography>
            {sourceText(sources[field], t, fmtDate) && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {sourceText(sources[field], t, fmtDate)}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
      {result.familyHistory?.relatives?.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" gutterBottom>
            {t('Family history on the pedigree')}
          </Typography>
          <FamilyHistoryEvidence
            relatives={result.familyHistory.relatives}
            memberRecords={memberRecords}
            anonymous={anonymous}
          />
        </>
      )}
      <Qrisk3Disclaimer />
    </Box>
  );
}
